import { useCallback, useEffect, useRef, useState } from 'react';
import { formatDateTime } from '../../../lib/formatDate';
import { errorMessage, toastError, toastInfo, toastSuccess } from '../../../lib/toast';
import { fetchOrgChart, fetchRefreshStatus, refreshOrgChart } from '../../orgChart/orgChartApi';
import '../../orgChart/orgChart.css';

const POLL_MS = 2000;
// Ceiling on how long a refresh can poll before giving up, mirroring
// OrgChartPage: a run that never reports finished would otherwise poll for as
// long as the admin tab stays open.
const MAX_POLL_ATTEMPTS = 150; // 150 * 2s = 5 minutes.

/**
 * Org chart mirror status + a manual refresh trigger, surfaced in the admin
 * Odoo sync area so admins don't have to leave to the org chart page itself
 * to see freshness or kick a refresh.
 *
 * `onWarnings` is called with the payload's `warnings` array and its
 * `warnings_out_of_scope` tally (both admin-only fields from GET
 * /api/org-chart) whenever it loads or refreshes, so the sibling data quality
 * panel can render from this one fetch instead of issuing its own identical
 * request.
 */
export default function OrgChartSyncPanel({ onWarnings }) {
  const [payload, setPayload] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const pollRef = useRef(null);
  const pollAttemptsRef = useRef(0);
  // Guards every post-await state write below. clearTimeout on unmount only
  // cancels a timer that has not fired yet; it cannot stop a poll callback
  // that is already awaiting fetchRefreshStatus() when this panel unmounts.
  // That in-flight callback still resolves and would otherwise call
  // setRefreshing/toastSuccess/load (which itself sets more state) on an
  // unmounted component, so every completion path below checks this ref
  // first and bails out silently if the panel is gone.
  const mountedRef = useRef(true);

  // Held in a ref so `load` below does NOT depend on this callback's identity.
  //
  // The parent passes an inline arrow, so `onWarnings` is a new function on
  // every parent render, and the callback itself sets parent state. With `load`
  // depending on it directly the chain was: load -> onWarnings -> parent
  // setState -> parent re-render -> new arrow -> new `load` -> the mount effect
  // re-runs -> load again, unbounded. That exhausted the API rate limit (300 per
  // window) within seconds, after which every request 429'd and the panel
  // rendered its load-failure message, which is why the status readout appeared
  // briefly and was then replaced by "Could not load the org chart sync status"
  // while the chart page itself, whose loader takes no parent callback, was fine.
  //
  // A ref keeps the latest callback reachable without making it a dependency.
  const onWarningsRef = useRef(onWarnings);
  onWarningsRef.current = onWarnings;

  const load = useCallback(async () => {
    try {
      const data = await fetchOrgChart();
      if (!mountedRef.current) return;
      setPayload(data);
      setFailed(false);
      const cb = onWarningsRef.current;
      if (cb) cb(data?.warnings || [], data?.warnings_out_of_scope || {});
    } catch {
      if (!mountedRef.current) return;
      // Load failures stay inline per the client conventions, never a toast.
      setFailed(true);
    } finally {
      if (mountedRef.current) setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, []);

  const poll = useCallback(() => {
    pollRef.current = setTimeout(async () => {
      pollAttemptsRef.current += 1;
      try {
        const state = await fetchRefreshStatus();
        if (!mountedRef.current) return;
        if (state.running) {
          if (pollAttemptsRef.current >= MAX_POLL_ATTEMPTS) {
            setRefreshing(false);
            toastInfo('The refresh is taking longer than expected.');
            return;
          }
          poll();
          return;
        }
        setRefreshing(false);
        toastSuccess('Org chart updated');
        load();
      } catch {
        if (!mountedRef.current) return;
        setRefreshing(false);
        toastError('Could not confirm the refresh finished.');
      }
    }, POLL_MS);
  }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    pollAttemptsRef.current = 0;
    try {
      await refreshOrgChart();
      if (!mountedRef.current) return;
      poll();
    } catch (err) {
      if (!mountedRef.current) return;
      setRefreshing(false);
      if (err?.response?.status === 409) toastInfo('A refresh is already running.');
      else toastError(errorMessage(err, 'Could not refresh the org chart.'));
    }
  }, [poll]);

  if (!loaded) return null;

  if (failed) {
    return (
      <section className="oc-companies">
        <p className="oc-companies-help">Could not load the org chart sync status.</p>
      </section>
    );
  }

  const nodeCount = payload?.nodes?.length || 0;
  const freshness = payload?.synced_at ? formatDateTime(payload.synced_at) : '';

  return (
    <section className="oc-companies">
      <label>Org chart mirror</label>
      <p className="oc-companies-help">
        The mirrored Odoo HR data that powers the org chart. Refresh pulls the latest
        jobs and employees from Odoo.
      </p>

      {nodeCount === 0 ? (
        <p className="oc-companies-help">
          The org chart mirror is empty. It populates on the nightly sync, or click
          Refresh now below.
        </p>
      ) : (
        <p className="oc-companies-names">
          {nodeCount} node{nodeCount === 1 ? '' : 's'} mirrored (jobs and employees)
          {freshness && ` · Updated ${freshness}`}
        </p>
      )}

      <div className="oc-companies-row">
        <button type="button" className="btn" disabled={refreshing} onClick={onRefresh}>
          {refreshing ? 'Refreshing...' : 'Refresh now'}
        </button>
      </div>
    </section>
  );
}
